import { OnInit, Directive } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import {filter, map, tap} from 'rxjs/operators';
import {CampHistoriqueModificationService} from '../../shared/service/camp-historique-modification.service';
import {SnackBarService} from '../../shared/service/snackbar.service';
import {convertToHumanDate} from '../../shared/util/moment.utils';
import {disableCodeSubmitButton, getFormArrayRowsSwitched} from '../../shared/util/form-validator.utils';
import {ActivatedRoute} from '@angular/router';
import {LocalCacheService} from '../../shared/service/local-cache.service';
import {ICampHistoModuleDerniereModificationContainer} from '../../shared/model/camp-historique-modification.model';
import {EventManager} from '../../shared/util/event-manager';
import {HistoModificationService} from '../../shared/service/histo-modification.service';
import { ICamp } from '../../shared/model/camp.model';
import { filterOkMapBody } from "../../shared/util/http.utils";
import { CampService } from "../../shared/service/camp.service";
import {CampModificationActionEnum} from "../../shared/constant/enum";

export enum SaveViewValueModuleTypeEnum {
  Static,
  Dynamic
}

@Directive()
export abstract class AbstractCampOngletComponent<T> implements OnInit {

  public isSaving = false;

  public convertToHumanDate = convertToHumanDate;

  public disableCodeSubmitButton = disableCodeSubmitButton;

  public getFormArrayRowsSwitched = getFormArrayRowsSwitched;

  public localCacheValue: ICampHistoModuleDerniereModificationContainer<T>;

  public idCamp: number;

  public campAdminstrationModules: ICamp;

  private localCacheInitialized = false;

  $subject = new Subject<string>();

  /**
   * Mémorisation de la version source serveur utilisée sur le save
   * afin de calculer les modifications apportées
   */
  protected campHistoSource: T;

  protected constructor(
    protected activatedRoute: ActivatedRoute,
    protected snackBarService: SnackBarService,
    protected histoModificationService: HistoModificationService,
    protected campService: CampService
  ) {
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe(paramMap => {
      const idCampStr = paramMap.get('idCamp');
      if (idCampStr) {
        this.localCacheValue = null; // réinitialisation du dernier histo qui va être mis à jour par le subscribe ci-dessous qui le récupère du serveur et / ou du localCache
        this.localCacheInitialized = false;

        const idCamp = this.idCamp = parseInt(idCampStr, 10);
        LocalCacheService.attachCampHistoModuleDerniereModificationContainerToLocalCache(
          // clé de cache
          this.getModuleCacheLocalCacheKey(idCamp),
          // Observable des valeur serveur
          this.findServerValue(idCamp)
            .pipe(
              tap((resp) => {
                this.campHistoSource = resp.body;
              }),
              CampHistoriqueModificationService.mapCampHistoModuleDerniereModificationHeader,
            ),
          // gestionnaire du cache
          {
            convertFromCache: (localCacheValue: T) => this.convertViewValueDateFromServer(localCacheValue),
            canClearCache: () => of(true) // FIXME la version du local storage est antérieur à celle du serveur, on doit donc la supprimer et avertir l'utilisateur
          },
          // Observable des valeurs clientes pour enregistrer dans le cache
          this.getViewValueChangesObservable()
            .pipe(
              filter(() => this.localCacheInitialized && !this.isSaving), // On ignore les valeurs provenant de la vue tant que le cache local n'a pas été initialisé ou au retour du serveur, pendant une sauvegarde
              map(viewValue => // On converti la viewValue en lui ajoutant le dernier histo reçu
                ({
                  data: viewValue,
                  campHistoModuleDerniereModification: this.localCacheValue && this.localCacheValue.campHistoModuleDerniereModification
                })
              )
            )
        )
          .subscribe((localCacheValue) => {
            this.localCacheValue = localCacheValue;
            console.log({text: `AbstractCampOngletComponent.ngOnInit.subscribe`, localCacheValue});
            this.updateViewValue(localCacheValue.data);
            this.localCacheInitialized = true;
          });
      }
    });

    // TODO -- This should be corrected and replaced by a merge
    EventManager.campModulesUpdated.subscribe((camp: ICamp) => {
      this.campAdminstrationModules = camp;
    });
  }

  protected abstract getViewValueChangesObservable(): Observable<T>;

  /**
   * Retourne un observable d'une réponse serveur. Attention, la réponse doit avoir des dates converties
   */
  protected abstract findServerValue(idCamp: number): Observable<HttpResponse<T>>;

  protected abstract convertViewValueDateFromServer(viewValue: T): T;

  protected abstract updateViewValue(viewValue: T): void;

  protected abstract getModuleCacheLocalCacheKey(idCamp: number): string;

  protected saveViewValue(moduleType: SaveViewValueModuleTypeEnum, viewValue: T, action? : CampModificationActionEnum) {
    // Appel traitement de calcul de l'histo Modification
    const modificationJson = this.histoModificationService.computeCampHistorisableDataModifications(viewValue, this.campHistoSource);
    // Mise à jour des données
    this.subscribeToSaveResponse(this.updateServerWithViewValue(viewValue, modificationJson, action));
  }

  protected abstract updateServerWithViewValue(viewValue: T, modificationJson: object, action?: CampModificationActionEnum): Observable<HttpResponse<T>>;

  protected subscribeToSaveResponse(responseObservable: Observable<HttpResponse<T>>, reInitialize: boolean = false) {
    responseObservable.subscribe(response => this.onSaveSuccess(response, reInitialize), () => this.onSaveError());
  }

  protected onSaveSuccess(response: HttpResponse<T>, reInitialize: boolean = false) {
    this.campService.savingEmitter.next(true);
    this.snackBarService.showSuccess('Les données ont été enregistrées avec succès', {durationInSeconds: 2});
    LocalCacheService.clearLocalCacheValueForKey(this.getModuleCacheLocalCacheKey(this.idCamp));

    const serverValue = CampHistoriqueModificationService.httpResponseToCampHistoModuleDerniereModificationContainer(response);
    serverValue.data = this.convertViewValueDateFromServer(serverValue.data);
    this.localCacheValue = serverValue;
    this.campHistoSource = response.body;
    this.updateViewValue(serverValue.data);
    if(serverValue && serverValue.data && !serverValue.data['camp']) {
      EventManager.campOngletUpdated.next(serverValue.data);
    }
    this.isSaving = false;
    if(reInitialize === true) {
      // We call this just in case of teledeclaration
      LocalCacheService.clearLocalCacheValueForKey(this.getModuleCacheLocalCacheKey(this.idCamp));
        return this.campService.findOneByIdAndModuleCode(this.idCamp)
          .pipe(
            filterOkMapBody
          )
          .subscribe(response => {
            this.$subject.next('true');
          })
    }
  }

  protected onSaveError() {
    this.isSaving = false;
  }

}
